import {
  WSElement,
  WSEmptyState,
  WSFilters,
  WSInfiniteScroll
} from "@wingspanhq/fe-component-library";
import {
  BatchType,
  CustomFieldResourceType,
  SearchPayeeRowsFilterParameter
} from "@wingspanhq/wingspan-api-sdk";
import {
  queriesBatches,
  queriesCustomFields,
  queriesEngagements,
  queriesPayeeRows,
  queriesRequirementDefinitions
} from "@query";
import React, { useMemo } from "react";
import { RouteComponentProps } from "react-router-dom";
import { useQueryFeatureFlags } from "@hooks/useQueryFeatureFlags";
import { useBrowserPageTitle } from "../../../../shared/hooks/useBrowserPageTitle";
import { useUrlQueryFilters } from "../../../../shared/hooks/useUrlQueryFilters";
import { useXWingspanExpansion } from "../../../../shared/hooks/useXWingspanExpansion";
import { useGoToContractorImportStart } from "../../../BulkImporter/paths";
import { useCustomization } from "../../../customization";
import { useCreateContractorModal } from "../../components/CreateContractorModal/useCreateContractorModal";
import { TablePayees } from "../../components/TablePayees/TablePayees";
import {
  PAYEES_ARCHIVED_PATH,
  PAYEES_COMPLETE_PATH,
  PAYEES_INCOMPLETE_PATH,
  PAYEES_NOT_SIGNED_UP_PATH
} from "../../utils";
import {
  ContractorsTabsFilters,
  ContractorTab
} from "../RouteDashboard/DashboardTabs";
import { AlertBulkReview } from "./AlertBulkReview";
import { buildConfig, getInterceptFiltersChange } from "./filters/config";
import { createListRequest } from "./filters/request";
import { PayeeFilterEligibilityStatus, PayeeFilters } from "./filters/types";

/**
 * The dashboard tab paths that carry a fixed v3 filter. The "Active" tab
 * (`PAYEES_ALL_PATH`) is absent — it uses the sidebar filters instead, matching
 * the v1 behavior.
 */
const TAB_BY_PATH: Record<string, ContractorTab> = {
  [PAYEES_INCOMPLETE_PATH]: "incomplete",
  [PAYEES_COMPLETE_PATH]: "complete",
  [PAYEES_ARCHIVED_PATH]: "archived",
  [PAYEES_NOT_SIGNED_UP_PATH]: "notSignedUp"
};

export const RoutePayees: React.FC<RouteComponentProps> = props => {
  const { terminology } = useCustomization();
  useBrowserPageTitle(terminology().sendPaymentsContractor);

  const { filters, setFilters } = useUrlQueryFilters<PayeeFilters>();
  const xWingspanAccount = useXWingspanExpansion();
  const modalCreateContractor = useCreateContractorModal();
  const goToContractorImportStart = useGoToContractorImportStart();

  const queryFeatureFlags = useQueryFeatureFlags();
  const queryCustomFields = queriesCustomFields.listCustomFields.useQueryAll({
    filter: { resourceType: { eq: CustomFieldResourceType.PAYEE } }
  });
  const queryEngagementList = queriesEngagements.listEngagements.useQueryAll(
    undefined,
    { refetchOnMount: false, retry: false }
  );
  // v3 `listBatches` takes no filter, so the contractor-import batches are
  // narrowed client-side on `type` (was a dedicated v1 bulk-collaborator
  // collection). Paged to completion: the filter dropdown must list them all.
  const queryBulkBatches = queriesBatches.listBatches.useQueryAll({});
  const bulkBatches = useMemo(
    () =>
      (queryBulkBatches.flatData ?? []).filter(
        batch => batch.type === BatchType.PAYEE_IMPORT
      ),
    [queryBulkBatches.flatData]
  );
  const queryRequirements =
    queriesRequirementDefinitions.listRequirementDefinitions.useQueryAll();

  const customFields = queryCustomFields.flatData;

  const tab = TAB_BY_PATH[props.match.path];
  const hasTabFilters = !!tab;

  const filter: SearchPayeeRowsFilterParameter = useMemo(
    () =>
      tab
        ? ContractorsTabsFilters[tab]
        : createListRequest({ ...filters }, customFields).filter,
    [customFields, filters, tab]
  );

  const queryPayeeRows = queriesPayeeRows.searchPayeeRows.usePaginatedQuery({
    filter,
    xWingspanAccount
  });
  const payees = queryPayeeRows.flatData ?? [];
  const totalCount = queryPayeeRows.data?.pages?.[0]?.pagination?.totalSize;

  // Gate the whole filter bar on there being any contractor at all (any tab),
  // mirroring the v1 "all payees" summary gate.
  const queryAnyPayees = queriesPayeeRows.searchPayeeRows.useQuery(
    {
      filter: ContractorsTabsFilters.all,
      page: { size: 1 },
      xWingspanAccount
    },
    { select: list => list.pagination?.totalSize }
  );

  const primaryFiltersConfig = useMemo(
    () =>
      buildConfig({
        engagements: (queryEngagementList.flatData || []).filter(
          e => !e.isDefault
        ),
        bulkBatches,
        customFields,
        requirements: queryRequirements.flatData ?? []
      }),
    [
      bulkBatches,
      customFields,
      queryEngagementList.flatData,
      queryRequirements.flatData
    ]
  );

  const interceptFiltersChange = useMemo(
    () => getInterceptFiltersChange(queryRequirements.flatData ?? []),
    [queryRequirements.flatData]
  );

  const hasActiveFilters = Object.keys(filters).length > 0;

  const renderBody = () => {
    if (queryPayeeRows.isLoading) {
      return (
        <WSElement mt="M">
          <TablePayees payees={[]} loading isArchivedOnly={hasTabFilters} />
        </WSElement>
      );
    }

    if (queryPayeeRows.isError) {
      return (
        <WSEmptyState
          py="3XL"
          mt="L"
          hasBorder
          bgColor="gray"
          type="error"
          title="Couldn’t load contractors"
          description="Something went wrong while loading contractors."
          actions={{
            buttons: [
              {
                label: "Try again",
                kind: "Secondary",
                onClick: () => queryPayeeRows.refetch()
              }
            ]
          }}
        />
      );
    }

    if (payees.length === 0) {
      return hasActiveFilters ? (
        <WSEmptyState
          py="3XL"
          mt="L"
          hasBorder
          bgColor="gray"
          type="search"
          title="No results found"
          description="Try adjusting your search or filter to find what you’re looking for."
        />
      ) : (
        <WSEmptyState
          py="3XL"
          mt="L"
          hasBorder
          type="people"
          bgColor="gray"
          title="No contractors yet"
          description={
            hasTabFilters
              ? undefined
              : "Once a contractor is added, they will appear here."
          }
          actions={
            hasTabFilters
              ? undefined
              : {
                  buttons: [
                    {
                      label: "Add contractor",
                      kind: "Secondary",
                      onClick: () => {
                        modalCreateContractor.open({ redirectToDetails: true });
                      }
                    },
                    {
                      label: "Bulk upload contractors",
                      kind: "Tertiary",
                      onClick: async () => {
                        goToContractorImportStart();
                      }
                    }
                  ]
                }
          }
        />
      );
    }

    return (
      <>
        {queryFeatureFlags.data?.requirementsBulkReview &&
          (filters.eligibilityStatus ===
            PayeeFilterEligibilityStatus.Incomplete ||
            filters.eligibilityStatus ===
              PayeeFilterEligibilityStatus.SpecificRequirement) && (
            <AlertBulkReview payeeFilters={filters} />
          )}
        <WSInfiniteScroll
          mt="M"
          onLoad={() => queryPayeeRows.fetchNextPage()}
          loadMore={payees.length > 0}
          endOfList={!queryPayeeRows.hasNextPage}
          loading={!!queryPayeeRows.isFetchingNextPage}
        >
          <TablePayees payees={payees} isArchivedOnly={hasTabFilters} />
        </WSInfiniteScroll>
      </>
    );
  };

  return (
    <>
      {!!queryAnyPayees.data && (
        <WSFilters<PayeeFilters>
          my="L"
          searchable
          values={filters}
          onFilter={setFilters}
          interceptChange={interceptFiltersChange}
          primaryFilters={primaryFiltersConfig}
          hidePrimaryFiltersButton={hasTabFilters}
          info={totalCount === undefined ? undefined : { count: totalCount }}
        />
      )}
      {renderBody()}
    </>
  );
};
